<template>
  <mf-card-container :title="text.title" :description="text.description">
    <template v-slot:title-icon>
      <div class="title-icon-container d-flex align-center justify-center" :style="`background: ${icon.bgColor}`">
        <mf-icon :icon="icon.name" :color="icon.color" size="24" />
      </div>
    </template>
    <card-empty-state v-if="isEmpty" icon="view_carousel" height="200px" class="mt-4" />
    <v-data-table
      v-else
      :key="`${variant}-products-table`"
      style="width: 100%"
      fixed-header
      disable-pagination
      hide-default-footer
      :headers="headers"
      :items="items"
      class="mt-5"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td class="d-flex justify-center align-center">
            <img v-if="item.image" class="img-container" :src="item.image" :alt="`${item.name} image`" />
            <div v-else class="icon-container d-flex justify-center align-center">
              <mf-icon size="20" color="cinza-d10">image_not_supported</mf-icon>
            </div>
          </td>
          <td>
            <mf-tooltip top max-width="300px">
              <template v-slot:activator="{ on }">
                <div class="ellipsis-container">
                  <a target="_blank" class="link text-ellipsis" :href="`#/produtos/detalhes-produto/${item.sku}`">{{ item.name }}</a>
                </div>
              </template>
              {{ item.name }}
            </mf-tooltip>
          </td>
          <td v-if="variant !== 'out'">
            <span>{{ item.days === 1 ? '1 dia' : `${item.days} dias` }}</span>
          </td>
          <td v-if="variant !== 'out'">
            <span
              :class="{
                'color-best': item.incrementalRevenue > 0,
                'color-neutral': item.incrementalRevenue === 0,
                'color-worst': item.incrementalRevenue < 0,
              }"
              >{{ getIncrementalRevenueNegative(item.incrementalRevenue)
              }}{{ getIncrementalRevenue(item.incrementalRevenue) | localeString(0) | currency }}</span
            >
          </td>
          <td>
            <span>{{ item.revenue | localeString(0) | currency }}</span>
          </td>
          <td>
            <mf-stonks variant="percentage" :percentage="item.increase * 100" />
          </td>
          <td v-if="variant !== 'out'">
            <span>{{ item.firstBuy | localeString | hifen }}</span>
          </td>
        </tr>
      </template>
    </v-data-table>
  </mf-card-container>
</template>

<script>
import MfStonks from '@/modules/maria/produtos-impulsionadores/views/components/Step1/MfStonks.vue'
import CardEmptyState from '@/components/CardEmptyState.vue'
export default {
  name: 'ProductOffersTable',
  components: { MfStonks, CardEmptyState },
  props: {
    variant: {
      type: String,
      default: 'best',
      validator: (value) => ['', 'best', 'worst', 'out'].includes(value),
    },
    products: {
      type: Array,
      default: () => [],
    },
    isEmpty: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    items() {
      return this.products.map((item) => {
        return {
          sku: item?.product?.sku,
          name: item?.product?.name,
          image: item?.product?.image,
          days: item?.metrics?.daysOnOffer?.quantity,
          revenue: item?.metrics?.revenue,
          increase: item?.metrics?.uplift,
          firstBuy: item?.metrics?.firstBuy?.quantity,
          incrementalRevenue: item?.metrics?.incrementalRevenue,
        }
      })
    },
    headers() {
      const headers = [
        {
          text: '',
          value: 'image',
          width: '60px',
          sortable: false,
        },
        {
          text: 'Produto',
          value: 'name',
          width: '200px',
          sortable: false,
        },
        ...(this.variant !== 'out'
          ? [
              {
                text: 'Dias em oferta',
                value: 'days',
                width: '',
                sortable: false,
              },
            ]
          : []),
        ...(this.variant !== 'out'
          ? [
              {
                text: 'Fat. incremental',
                value: 'incrementalRevenue',
                width: '',
                sortable: false,
              },
            ]
          : []),
        {
          text: 'Faturamento',
          value: 'revenue',
          width: '',
          sortable: false,
        },
        {
          text: 'Aumento das vendas',
          value: 'increase',
          width: '',
          sortable: false,
        },
        ...(this.variant !== 'out'
          ? [
              {
                text: 'Primeiras compras',
                value: 'firstBuy',
                width: '',
                sortable: false,
              },
            ]
          : []),
      ]
      return headers
    },
    icon() {
      const icons = {
        best: {
          name: 'trending_up',
          color: 'verde-d20',
          bgColor: '#0FC94D4D',
        },
        worst: {
          name: 'trending_down',
          color: 'vermelho-d20',
          bgColor: '#F47F6833',
        },
        out: {
          name: 'rocket_launch',
          color: 'marca-1-d30',
          bgColor: '#00F5CC26',
        },
      }

      return icons[this.variant]
    },
    text() {
      const texts = {
        best: {
          title: 'Melhores produtos em oferta',
          description: '5 melhores produtos em ofertas do clube no mês selecionado, baseado no faturamento incremental',
        },
        worst: {
          title: 'Piores produtos em oferta',
          description: '5 piores produtos em ofertas do clube no mês selecionado, baseado no faturamento incremental',
        },
        out: {
          title: 'Melhores produtos fora de ofertas do clube',
          description: '5 melhores produtos fora de ofertas do clube no mês selecionado, baseado no aumentos das vendas',
        },
      }

      return texts[this.variant]
    },
  },
  methods: {
    getIncrementalRevenue(incrementalRevenue) {
      return Math.abs(incrementalRevenue)
    },
    getIncrementalRevenueNegative(incrementalRevenue) {
      return incrementalRevenue < 0 ? '-' : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.title-icon-container {
  background: rgba($verde-l20, 0.3);
  width: 36px;
  height: 36px;
  border-radius: 4px;
}

.img-container,
.icon-container {
  width: 32px;
  height: 32px;
  border: 1px solid $cinza-l60;
  border-radius: 4px;
}

.icon-container {
  background-color: $cinza-l90;
  border: none;
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
  padding: 0 8px;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
    padding: 0 8px;
  }
}

.color-best {
  color: $verde-0;
}

.color-worst {
  color: $vermelho-0;
}

.color-neutral {
  color: $cinza-0;
}

.ellipsis-container {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: $marca-0-l40;
  text-decoration: underline;
}
</style>
